<script>
/**
 * @component ProceduresList
 * @description UC-REG-160 — Bandeja del staff: "Gestiones de trámites".
 *              Filtros arriba (search + estado + tipo) tipo AuthorizationList.
 *              Click en fila abre modal detalle con chat de ida y vuelta +
 *              textarea para responder + botón "Cerrar caso".
 *
 * @example <ProceduresList />
 */
import procedureService, { procedureTypeService } from "@/services/procedure";
import { format } from "date-fns";
import { mapGetters } from "vuex";

export default {
  name: "ProceduresList",

  data() {
    return {
      procedures: [],
      types: [],
      loading: false,
      searchInput: "",
      statusFilter: "",
      typeFilter: "",
      searchDebounce: null,

      detail: {
        visible: false,
        procedure: null,
        newMessage: "",
        // Adjuntos que van en el próximo mensaje del staff. Se envían
        // junto con el texto al hacer click en Enviar respuesta.
        newFiles: [],
        // Canal por el que se notifica al submitter cuando el staff responde.
        // Default 'email' (más confiable). El operador puede cambiarlo por
        // respuesta — evita spamear ambos canales al paciente.
        notifyChannel: "email",
        sending: false,
        closing: false,
      },

      // Modal "Nuevo trámite" — el staff carga en nombre de un paciente.
      // Search de pacientes: `patientQuery` con debounce dispara
      // `patientService.get({ $text: { $search } })`. El staff elige uno del
      // dropdown y ese Patient._id viaja al backend como submitterPatientId.
      newProc: {
        visible: false,
        saving: false,
        patientQuery: "",
        patientResults: [],
        patientSearching: false,
        patientDebounce: null,
        // Canal por el que se le avisa al paciente que hay un trámite
        // nuevo creado a su nombre. Default email (más confiable).
        notifyChannel: "email",
        form: {
          submitterPatient: null,
          procedureType: "",
          description: "",
          files: [],
        },
      },
    };
  },

  computed: {
    ...mapGetters(["user"]),
  },

  created() {
    this.load();
    this.loadTypes();
  },

  watch: {
    statusFilter() {
      this.load();
    },
    typeFilter() {
      this.load();
    },
    searchInput() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.load(), 350);
    },
  },

  methods: {
    async load() {
      this.loading = true;
      try {
        const params = {};
        if (this.statusFilter) params.status = this.statusFilter;
        if (this.typeFilter) params.procedureType = this.typeFilter;
        if (this.searchInput.trim()) params.search = this.searchInput.trim();
        this.procedures = await procedureService.list(params);
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo cargar la bandeja"
        );
      } finally {
        this.loading = false;
      }
    },

    async loadTypes() {
      try {
        this.types = await procedureTypeService.list();
      } catch (err) {
        // Non-fatal: el filtro por tipo queda vacío pero la bandeja funciona.
      }
    },

    clearFilters() {
      this.searchInput = "";
      this.statusFilter = "";
      this.typeFilter = "";
    },

    /**
     * @description Abre el modal "Nuevo trámite" para que el staff cargue
     *              uno en nombre de un paciente (misma capacidad que el
     *              portal del paciente).
     */
    openNew() {
      if (this.types.length === 0) {
        this.$message.warning(
          "No hay tipos de trámite activos. Cargá al menos uno desde Tipos de trámite."
        );
        return;
      }
      this.newProc = {
        visible: true,
        saving: false,
        patientQuery: "",
        patientResults: [],
        patientSearching: false,
        patientDebounce: null,
        notifyChannel: "email",
        form: {
          submitterPatient: null,
          procedureType: "",
          description: "",
          files: [],
        },
      };
    },
    closeNew() {
      this.newProc.visible = false;
    },

    onPatientQueryInput() {
      if (this.newProc.patientDebounce) {
        clearTimeout(this.newProc.patientDebounce);
      }
      this.newProc.patientDebounce = setTimeout(
        () => this.searchPatients(),
        300
      );
    },
    async searchPatients() {
      const q = this.newProc.patientQuery.trim();
      if (q.length < 2) {
        this.newProc.patientResults = [];
        return;
      }
      this.newProc.patientSearching = true;
      try {
        // Endpoint dedicado del módulo: /procedures/patients-search.
        // No requiere permiso `patient.query` (que no todos los roles
        // staff tienen) y devuelve identidad completa (email/phone) que
        // usamos para armar la notificación al crear.
        const res = await procedureService.searchPatients(q);
        this.newProc.patientResults = Array.isArray(res) ? res : [];
      } catch (err) {
        this.newProc.patientResults = [];
      } finally {
        this.newProc.patientSearching = false;
      }
    },
    pickPatient(p) {
      this.newProc.form.submitterPatient = p;
      this.newProc.patientResults = [];
      this.newProc.patientQuery = this.formatPatientLabel(p);
    },
    /**
     * @description Formato del chip que muestra el paciente seleccionado.
     *              Nombre + DNI. Los tokens vienen del endpoint dedicado
     *              `/procedures/patients-search`.
     */
    formatPatientLabel(p) {
      if (!p) return "";
      const name = [p.firstName, p.lastName].filter(Boolean).join(" ");
      const dni = p.governmentId?.number
        ? ` — DNI ${p.governmentId.number}`
        : "";
      return `${name}${dni}`;
    },
    onNewProcFiles(event) {
      const picked = Array.from(event.target.files || []);
      picked.forEach((f) => {
        if (f.size > 15 * 1024 * 1024) {
          this.$message.warning(`"${f.name}" supera los 15MB, no se adjunta`);
          return;
        }
        if (this.newProc.form.files.length >= 10) {
          this.$message.warning("Máximo 10 adjuntos");
          return;
        }
        this.newProc.form.files.push(f);
      });
      event.target.value = "";
    },
    removeNewProcFile(index) {
      this.newProc.form.files.splice(index, 1);
    },
    /**
     * @description Envía el POST /procedures armado por el admin. El backend
     *              resuelve el User del paciente y usa esa referencia como
     *              submitter (submitterType='patient').
     */
    async submitNewProc() {
      const { submitterPatient, procedureType, description, files } =
        this.newProc.form;
      if (!submitterPatient) {
        this.$message.warning("Elegí un paciente");
        return;
      }
      if (!procedureType) {
        this.$message.warning("Elegí un tipo de trámite");
        return;
      }
      if (!description.trim()) {
        this.$message.warning("Escribí una descripción");
        return;
      }
      this.newProc.saving = true;
      try {
        const form = new FormData();
        form.append("submitterPatientId", submitterPatient.patientId);
        form.append("procedureType", procedureType);
        form.append("description", description.trim());
        form.append("notify", this.newProc.notifyChannel);
        files.forEach((f) => form.append("files", f, f.name));
        await procedureService.create(form);
        this.$message.success("Trámite creado");
        this.newProc.visible = false;
        await this.load();
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo crear el trámite"
        );
      } finally {
        this.newProc.saving = false;
      }
    },

    // ═══ Chat ═══
    onPickChatFiles(event) {
      const picked = Array.from(event.target.files || []);
      picked.forEach((f) => {
        if (f.size > 15 * 1024 * 1024) {
          this.$message.warning(`"${f.name}" supera los 15MB, no se adjunta`);
          return;
        }
        if (this.detail.newFiles.length >= 10) {
          this.$message.warning("Máximo 10 adjuntos por mensaje");
          return;
        }
        this.detail.newFiles.push(f);
      });
      event.target.value = "";
    },
    removeChatFile(index) {
      this.detail.newFiles.splice(index, 1);
    },
    formatFileSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024) return `${bytes}B`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    },

    async openDetail(row) {
      try {
        const full = await procedureService.getById(row._id);
        this.detail = {
          visible: true,
          procedure: full,
          newMessage: "",
          newFiles: [],
          notifyChannel: "email",
          sending: false,
          closing: false,
        };
        this.$nextTick(() => this.scrollChatToBottom());
      } catch (err) {
        this.$message.error("No se pudo abrir el trámite");
      }
    },
    close() {
      this.detail.visible = false;
    },

    async sendMessage() {
      const text = this.detail.newMessage.trim();
      const files = this.detail.newFiles;
      if (!text && files.length === 0) return;
      this.detail.sending = true;
      try {
        const updated = await procedureService.addMessage(
          this.detail.procedure._id,
          text,
          this.detail.notifyChannel,
          files
        );
        this.detail.procedure = updated;
        this.detail.newMessage = "";
        this.detail.newFiles = [];
        await this.load();
        this.$nextTick(() => this.scrollChatToBottom());
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo enviar el mensaje"
        );
      } finally {
        this.detail.sending = false;
      }
    },

    /**
     * @description Cierra el caso — acción terminal, confirma antes.
     */
    async closeCase() {
      const ok = await this.$confirm(
        "¿Cerrar este trámite? Después no se podrán agregar más mensajes de ninguno de los lados.",
        "Cerrar caso",
        {
          confirmButtonText: "Cerrar caso",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      ).catch(() => false);
      if (!ok) return;
      this.detail.closing = true;
      try {
        const updated = await procedureService.close(this.detail.procedure._id);
        this.detail.procedure = updated;
        await this.load();
        this.$message.success("Trámite cerrado");
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo cerrar el trámite"
        );
      } finally {
        this.detail.closing = false;
      }
    },

    scrollChatToBottom() {
      const el = document.querySelector(".proc-list__chat");
      if (el) el.scrollTop = el.scrollHeight;
    },

    async downloadFile(fileEntry) {
      try {
        const { url } = await procedureService.getFileSignedUrl(
          this.detail.procedure._id,
          fileEntry.key
        );
        window.open(url, "_blank");
      } catch (err) {
        this.$message.error("No se pudo abrir el adjunto");
      }
    },

    formatDate(date) {
      if (!date) return "—";
      try {
        return format(new Date(date), "dd/MM/yyyy HH:mm");
      } catch (e) {
        return "—";
      }
    },

    submitterName(submitter) {
      if (!submitter) return "—";
      const parts = [submitter.firstName, submitter.lastName]
        .filter(Boolean)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());
      return parts.join(" ") || submitter.email || "—";
    },
    submitterTypeLabel(t) {
      if (t === "patient") return "Paciente";
      if (t === "provider") return "Prestador";
      return "—";
    },
    statusLabel(status) {
      if (status === "closed") return "Cerrado";
      if (status === "answered") return "Respondido";
      return "Pendiente";
    },
    statusClass(status) {
      if (status === "closed") return "is-closed";
      if (status === "answered") return "is-answered";
      return "is-pending";
    },

    isStaffMessage(msg) {
      return msg && msg.authorRole === "staff";
    },
  },
};
</script>

<template lang="pug">
.proc-list
  .proc-list__header
    h2.proc-list__title Gestiones de trámites
    button.proc-list__new(@click="openNew") + Nuevo trámite

  .proc-list__filters
    input.proc-list__search(
      type="text"
      v-model="searchInput"
      placeholder="Buscar por descripción o solicitante"
    )
    select.proc-list__filter(v-model="statusFilter")
      option(value="") Todos los estados
      option(value="pending") Pendientes
      option(value="answered") Respondidos
      option(value="closed") Cerrados
    select.proc-list__filter(v-model="typeFilter")
      option(value="") Todos los tipos
      option(v-for="t in types" :key="t._id" :value="t._id") {{ t.name }}
    button.proc-list__clear(v-if="searchInput || statusFilter || typeFilter" @click="clearFilters") Limpiar

  .proc-list__empty(v-if="!loading && procedures.length === 0")
    p No hay trámites que coincidan con estos filtros.

  table.proc-list__table(v-if="procedures.length > 0")
    thead
      tr
        th Fecha
        th Solicitante
        th Rol
        th Tipo
        th Estado
        th
    tbody
      tr(v-for="p in procedures" :key="p._id" @click="openDetail(p)")
        td.proc-list__date {{ formatDate(p.createdAt) }}
        td
          strong {{ submitterName(p.submitter) }}
          .proc-list__submitter-meta(v-if="p.submitter && p.submitter.governmentId && p.submitter.governmentId.number") DNI {{ p.submitter.governmentId.number }}
        td.proc-list__role {{ submitterTypeLabel(p.submitterType) }}
        td.proc-list__type {{ p.procedureType && p.procedureType.name || "—" }}
        td
          span.proc-list__badge(:class="statusClass(p.status)") {{ statusLabel(p.status) }}
        td.proc-list__arrow ›

  //- ═══ Modal detalle + chat + respuesta + cerrar ═══
  .proc-list__modal-backdrop(v-if="detail.visible" @click.self="close")
    .proc-list__modal(v-if="detail.procedure")
      .proc-list__modal-header
        h3 {{ detail.procedure.procedureType && detail.procedure.procedureType.name || "Trámite" }}
        span.proc-list__badge(:class="statusClass(detail.procedure.status)") {{ statusLabel(detail.procedure.status) }}
      .proc-list__view-block
        .proc-list__view-row
          span.proc-list__view-label Solicitante:
          span {{ submitterName(detail.procedure.submitter) }} ({{ submitterTypeLabel(detail.procedure.submitterType) }})
        .proc-list__view-row(v-if="detail.procedure.submitter && detail.procedure.submitter.email")
          span.proc-list__view-label Email:
          span {{ detail.procedure.submitter.email }}
        .proc-list__view-row
          span.proc-list__view-label Enviado:
          span {{ formatDate(detail.procedure.createdAt) }}

      .proc-list__chat
        .proc-list__msg.proc-list__msg--other
          .proc-list__msg-author {{ submitterName(detail.procedure.submitter) }}
          .proc-list__msg-bubble
            .proc-list__msg-text {{ detail.procedure.description }}
            .proc-list__msg-files(v-if="detail.procedure.files && detail.procedure.files.length > 0")
              a.proc-list__msg-file(v-for="(f, i) in detail.procedure.files" :key="i" href="#" @click.prevent="downloadFile(f)")
                | 📎 {{ f.originalName || f.key }}
          .proc-list__msg-meta {{ formatDate(detail.procedure.createdAt) }}

        .proc-list__msg(v-for="(m, idx) in detail.procedure.messages" :key="idx" :class="{ 'proc-list__msg--mine': isStaffMessage(m), 'proc-list__msg--other': !isStaffMessage(m) }")
          .proc-list__msg-author(v-if="!isStaffMessage(m)")
            | {{ submitterName(detail.procedure.submitter) }}
          .proc-list__msg-author(v-else)
            | {{ m.author && ((m.author.firstName || '') + ' ' + (m.author.lastName || '')).trim() || 'Centro médico' }}
          .proc-list__msg-bubble
            .proc-list__msg-text(v-if="m.text") {{ m.text }}
            .proc-list__msg-files(v-if="m.files && m.files.length > 0")
              a.proc-list__msg-file(v-for="(f, i) in m.files" :key="i" href="#" @click.prevent="downloadFile(f)")
                | 📎 {{ f.originalName || f.key }}
          .proc-list__msg-meta {{ formatDate(m.createdAt) }}

      .proc-list__compose-block(v-if="detail.procedure.status !== 'closed'")
        .proc-list__compose
          textarea(
            v-model="detail.newMessage"
            rows="3"
            maxlength="4000"
            placeholder="Escribí una respuesta para el solicitante…"
            :disabled="detail.sending || detail.closing"
          )
          button.proc-list__btn.proc-list__btn--primary(@click="sendMessage" :disabled="detail.sending || detail.closing || (!detail.newMessage.trim() && detail.newFiles.length === 0)")
            | {{ detail.sending ? "Enviando…" : "Enviar respuesta" }}
        //- Fila de adjuntos: botón + chips de archivos ya elegidos.
        .proc-list__attach-row
          label.proc-list__attach-btn(:class="{ 'is-disabled': detail.sending || detail.closing }")
            micon(name="attach_file")
            span Adjuntar
            input(type="file" multiple @change="onPickChatFiles" :disabled="detail.sending || detail.closing" hidden)
          .proc-list__attach-chips(v-if="detail.newFiles.length > 0")
            .proc-list__attach-chip(v-for="(f, i) in detail.newFiles" :key="i")
              span 📎 {{ f.name }} ({{ formatFileSize(f.size) }})
              button(type="button" @click="removeChatFile(i)" :disabled="detail.sending || detail.closing") ×
        //- Selector de canal — evita spamear ambos. Default email.
        .proc-list__notify
          span.proc-list__notify-label Notificar por:
          label.proc-list__notify-opt
            input(type="radio" value="email" v-model="detail.notifyChannel")
            span Email
          label.proc-list__notify-opt
            input(type="radio" value="whatsapp" v-model="detail.notifyChannel")
            span WhatsApp
          label.proc-list__notify-opt
            input(type="radio" value="none" v-model="detail.notifyChannel")
            span No notificar
      .proc-list__closed-note(v-else)
        | Este trámite fue cerrado el {{ formatDate(detail.procedure.closedAt) }}
        template(v-if="detail.procedure.closedBy")
          |  por {{ detail.procedure.closedBy.firstName }} {{ detail.procedure.closedBy.lastName }}
        | .

      .proc-list__modal-actions
        button.proc-list__btn.proc-list__btn--danger(v-if="detail.procedure.status !== 'closed'" @click="closeCase" :disabled="detail.sending || detail.closing")
          | {{ detail.closing ? "Cerrando…" : "Cerrar caso" }}
        button.proc-list__btn.proc-list__btn--secondary(@click="close") Cerrar ventana

  //- ═══ Modal Nuevo trámite (admin en nombre de un paciente) ═══
  .proc-list__modal-backdrop(v-if="newProc.visible" @click.self="closeNew")
    .proc-list__modal
      .proc-list__modal-header
        h3 Nuevo trámite
      .proc-list__field
        label Paciente *
        input.proc-list__patient-search(
          type="text"
          v-model="newProc.patientQuery"
          placeholder="Buscá por nombre, apellido o DNI"
          @input="onPatientQueryInput"
        )
        .proc-list__patient-results(v-if="newProc.patientResults.length > 0 && !newProc.form.submitterPatient")
          .proc-list__patient-result(v-for="p in newProc.patientResults" :key="p.userId" @click="pickPatient(p)")
            strong {{ p.firstName }} {{ p.lastName }}
            span(v-if="p.governmentId && p.governmentId.number")  · DNI {{ p.governmentId.number }}
            .proc-list__patient-contact
              span(v-if="p.email") 📧 {{ p.email }}
              span(v-if="p.phone" style="margin-left: 8px;") 📱 {{ p.phone }}
              span(v-if="!p.email && !p.phone" style="color: #d97706;") Sin contacto cargado
        .proc-list__patient-hint(v-if="newProc.patientSearching") Buscando…
        .proc-list__patient-hint(v-else-if="newProc.patientQuery.length >= 2 && newProc.patientResults.length === 0 && !newProc.form.submitterPatient") Sin resultados
        //- Muestra identidad completa del paciente elegido (útil para
        //- confirmar antes de crear y ver qué canales de contacto tiene).
        .proc-list__patient-picked(v-if="newProc.form.submitterPatient")
          span(v-if="newProc.form.submitterPatient.email") 📧 {{ newProc.form.submitterPatient.email }}
          span(v-if="newProc.form.submitterPatient.phone" style="margin-left: 10px;") 📱 {{ newProc.form.submitterPatient.phone }}
          span(v-if="!newProc.form.submitterPatient.email && !newProc.form.submitterPatient.phone" style="color: #d97706;") ⚠ Este paciente no tiene email ni teléfono cargados. No podrá recibir notificación.
      .proc-list__field
        label Tipo *
        select(v-model="newProc.form.procedureType")
          option(value="" disabled) Elegí un tipo
          option(v-for="t in types" :key="t._id" :value="t._id") {{ t.name }}
      .proc-list__field
        label Descripción *
        textarea(v-model="newProc.form.description" rows="4" maxlength="2000" placeholder="Motivo del trámite, datos relevantes…")
      .proc-list__field
        label Notificar al paciente por
        .proc-list__notify(style="margin-top: 0;")
          label.proc-list__notify-opt
            input(type="radio" value="email" v-model="newProc.notifyChannel")
            span Email
          label.proc-list__notify-opt
            input(type="radio" value="whatsapp" v-model="newProc.notifyChannel")
            span WhatsApp
          label.proc-list__notify-opt
            input(type="radio" value="none" v-model="newProc.notifyChannel")
            span No notificar
      .proc-list__field
        label Adjuntos (opcional)
        .proc-list__attach-row
          label.proc-list__attach-btn
            micon(name="attach_file")
            span Elegir archivos
            input(type="file" multiple @change="onNewProcFiles" hidden)
          .proc-list__attach-chips(v-if="newProc.form.files.length > 0")
            .proc-list__attach-chip(v-for="(f, i) in newProc.form.files" :key="i")
              span 📎 {{ f.name }} ({{ formatFileSize(f.size) }})
              button(type="button" @click="removeNewProcFile(i)") ×
      .proc-list__modal-actions
        button.proc-list__btn.proc-list__btn--secondary(@click="closeNew" :disabled="newProc.saving") Cancelar
        button.proc-list__btn.proc-list__btn--primary(@click="submitNewProc" :disabled="newProc.saving")
          | {{ newProc.saving ? "Creando…" : "Crear trámite" }}
</template>

<style lang="scss" scoped>
.proc-list {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}
.proc-list__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.proc-list__title {
  margin: 0;
  font-size: 20px;
  color: $navy;
}
.proc-list__new {
  background: $navy;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: darken($navy, 5%);
  }
}
.proc-list__field {
  margin-bottom: 14px;
  label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    margin-bottom: 4px;
  }
  select,
  textarea,
  input[type="text"] {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    background: white;
    &:focus {
      outline: none;
      border-color: $navy;
    }
  }
  textarea {
    resize: vertical;
  }
}
.proc-list__patient-search {
  width: 100%;
}
.proc-list__patient-results {
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
}
.proc-list__patient-result {
  padding: 8px 10px;
  cursor: pointer;
  font-size: 13px;
  color: $navy;
  border-top: 1px solid #f1f5f9;
  &:hover {
    background: #f8fafc;
  }
  &:first-child {
    border-top: none;
  }
}
.proc-list__patient-hint {
  font-size: 11px;
  color: #64748b;
  margin-top: 4px;
}
.proc-list__patient-contact {
  margin-top: 3px;
  font-size: 11px;
  color: #64748b;
}
.proc-list__patient-picked {
  margin-top: 6px;
  font-size: 12px;
  color: #334155;
  padding: 6px 10px;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 3px solid $navy;
}
.proc-list__attach-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 6px;
}
.proc-list__attach-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  color: #334155;
  background: white;
  cursor: pointer;
  &:hover {
    border-color: $navy;
    color: $navy;
  }
  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.proc-list__attach-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.proc-list__attach-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #f1f5f9;
  border-radius: 12px;
  font-size: 11px;
  color: #334155;
  button {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    padding: 0 2px;
    &:hover {
      color: #dc2626;
    }
  }
}
.proc-list__filters {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  align-items: center;
  flex-wrap: wrap;
}
.proc-list__search {
  flex: 1;
  min-width: 220px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  &:focus {
    outline: none;
    border-color: $navy;
  }
}
.proc-list__filter {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  min-width: 160px;
}
.proc-list__clear {
  background: none;
  border: none;
  color: $navy;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 8px;
  &:hover {
    text-decoration: underline;
  }
}
.proc-list__empty {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  font-size: 13px;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
}
.proc-list__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  thead th {
    text-align: left;
    padding: 12px;
    color: #64748b;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  tbody td {
    padding: 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
  }
  tbody tr {
    cursor: pointer;
    transition: background 0.12s;
    &:hover {
      background: #f8fafc;
    }
    &:last-child td {
      border-bottom: none;
    }
  }
}
.proc-list__date {
  color: #64748b;
  white-space: nowrap;
  font-size: 12px;
}
.proc-list__submitter-meta {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}
.proc-list__role {
  font-size: 12px;
  color: #64748b;
}
.proc-list__type {
  color: $navy;
  font-weight: 600;
}
.proc-list__arrow {
  color: #cbd5e1;
  text-align: right;
  width: 30px;
  font-size: 18px;
}
.proc-list__badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  &.is-pending {
    background: rgba(217, 119, 6, 0.12);
    color: #d97706;
  }
  &.is-answered {
    background: rgba(22, 163, 74, 0.12);
    color: #16a34a;
  }
  &.is-closed {
    background: #f1f5f9;
    color: #64748b;
  }
}
.proc-list__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.proc-list__modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 680px;
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}
.proc-list__modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  h3 {
    margin: 0;
    font-size: 16px;
    color: $navy;
  }
}
.proc-list__view-block {
  border-top: 1px solid #f1f5f9;
  padding-top: 12px;
  margin-bottom: 14px;
}
.proc-list__view-row {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 3px 0;
  font-size: 13px;
}
.proc-list__view-label {
  font-weight: 700;
  color: #334155;
  min-width: 100px;
}

/* Chat */
.proc-list__chat {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 12px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.proc-list__msg {
  display: flex;
  flex-direction: column;
  max-width: 78%;
  &--mine {
    align-self: flex-end;
    align-items: flex-end;
    .proc-list__msg-bubble {
      background: $navy;
      color: white;
    }
    .proc-list__msg-file {
      color: white;
      text-decoration: underline;
    }
  }
  &--other {
    align-self: flex-start;
    align-items: flex-start;
    .proc-list__msg-bubble {
      background: white;
      color: $navy;
      border: 1px solid #e2e8f0;
    }
  }
}
.proc-list__msg-author {
  font-size: 10.5px;
  color: #64748b;
  font-weight: 700;
  margin-bottom: 3px;
  padding: 0 4px;
}
.proc-list__msg-bubble {
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.4;
}
.proc-list__msg-text {
  white-space: pre-wrap;
  word-wrap: break-word;
}
.proc-list__msg-files {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.proc-list__msg-file {
  font-size: 12px;
  color: $navy;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}
.proc-list__msg-meta {
  font-size: 10px;
  color: #64748b;
  margin-top: 3px;
  padding: 0 4px;
}
.proc-list__compose-block {
  margin-bottom: 12px;
}
.proc-list__compose {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  textarea {
    flex: 1;
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    &:focus {
      outline: none;
      border-color: $navy;
    }
  }
  button {
    height: 42px;
    padding: 0 18px;
  }
}
.proc-list__notify {
  display: flex;
  gap: 14px;
  align-items: center;
  margin-top: 8px;
  padding: 6px 2px 0;
  font-size: 12px;
  color: #334155;
}
.proc-list__notify-label {
  color: #64748b;
  font-weight: 700;
}
.proc-list__notify-opt {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  input {
    margin: 0;
    cursor: pointer;
  }
}
.proc-list__closed-note {
  text-align: center;
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}
.proc-list__modal-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 4px;
}
.proc-list__btn {
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &--primary {
    background: $navy;
    color: white;
    &:hover:not(:disabled) {
      background: darken($navy, 5%);
    }
  }
  &--secondary {
    background: #f1f5f9;
    color: #334155;
    &:hover:not(:disabled) {
      background: #e2e8f0;
    }
  }
  &--danger {
    background: #dc2626;
    color: white;
    &:hover:not(:disabled) {
      background: darken(#dc2626, 5%);
    }
  }
}
</style>
